import { isEndoscopyService } from 'utils/serviceDoctorFilter';
import {
  diagnosesToSummaryText,
  EMPTY_AMBULATORY_PROTOCOL,
  normalizeDiagnosesList,
} from 'utils/ambulatoryProtocol';
import {
  defaultEndoscopyAnesthesia,
  resolveEndoscopyKind,
} from 'constants/endoscopyProtocolTemplates';
import {
  applySavedEndoscopyTemplateToDraft,
  resolveEndoscopyTemplate,
} from 'utils/endoscopyProtocolTemplates';
import { birthYearFromDate } from 'utils/patientBirth';
import { isInpatientBooking } from 'utils/bookingCareTypeSort';

export const EMPTY_ENDOSCOPY_META = {
  title: '',
  equipment: '',
  caseNumber: '',
  department: '',
  anesthesia: '',
};

export function isEndoscopyVisit(visitOrService) {
  if (!visitOrService) return false;
  if (typeof visitOrService === 'string') {
    return isEndoscopyService({ name: visitOrService });
  }
  const serviceName =
    visitOrService.serviceName ||
    visitOrService.service_name ||
    visitOrService.service?.name ||
    '';
  const categoryName =
    visitOrService.categoryName ||
    visitOrService.category_name ||
    visitOrService.service?.categoryName ||
    visitOrService.service?.category_name ||
    '';
  return isEndoscopyService({
    name: serviceName,
    categoryName,
    category_name: categoryName,
  });
}

export function normalizeEndoscopyMeta(raw) {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  return {
    title: String(source.title ?? '').trim(),
    equipment: String(source.equipment ?? '').trim(),
    caseNumber: String(source.caseNumber ?? source.case_number ?? '').trim(),
    department: String(source.department ?? '').trim(),
    anesthesia: String(source.anesthesia ?? '').trim(),
  };
}

export function endoscopyMetaHasContent(meta) {
  const m = normalizeEndoscopyMeta(meta);
  return Boolean(m.title || m.equipment || m.caseNumber || m.department || m.anesthesia);
}

export function formatEndoscopyDate(value) {
  if (!value) return '';
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}г`;
  const dmy = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
  if (dmy) {
    const dd = String(dmy[1]).padStart(2, '0');
    const mm = String(dmy[2]).padStart(2, '0');
    return `${dd}.${mm}.${dmy[3]}г`;
  }
  const d = new Date(`${raw.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return raw;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}г`;
}

export function formatEndoscopyTime(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const match = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return raw;
  const hh = String(match[1]).padStart(2, '0');
  const mm = match[2];
  const ss = match[3] || '00';
  return `${hh}:${mm}:${ss}`;
}

export function formatEndoscopyPatientLine(patient) {
  const p = patient && typeof patient === 'object' ? patient : {};
  const name = String(p.fullName || p.full_name || p.clientName || p.name || '').trim();
  const year = birthYearFromDate(p.birthDate || p.birth_date || '');
  if (name && year) return `${name} ${year}г.р.`;
  if (name) return name;
  if (year) return `${year}г.р.`;
  return '';
}

export function defaultEndoscopyDepartment(visit) {
  return isInpatientBooking(visit) ? 'хирургии' : 'амбулатория';
}

function endoscopyConclusionFromProtocol(protocol) {
  const summary = diagnosesToSummaryText(protocol?.diagnoses);
  return String(summary || protocol?.diagnosis || '').trim();
}

/** Черновик ЭГДС: шапка и мета; тело шаблона — только если пусто и injectTemplateBody. */
export function withEndoscopyProtocolDefaults(source, context = {}) {
  const protocol = source ? { ...EMPTY_AMBULATORY_PROTOCOL, ...source } : { ...EMPTY_AMBULATORY_PROTOCOL };
  const serviceName = context.serviceName || '';
  const savedTemplates = context.savedTemplates || [];
  const injectTemplateBody = context.injectTemplateBody !== false;
  const template = resolveEndoscopyTemplate(serviceName, savedTemplates);
  const savedMeta = normalizeEndoscopyMeta(protocol.protocolMeta);
  const defaults = {
    title: template.title || '',
    equipment: template.equipment || '',
    caseNumber: String(context.clientId || context.caseNumber || '').trim(),
    department: template.department || defaultEndoscopyDepartment(context.visit),
    anesthesia: template.anesthesia || defaultEndoscopyAnesthesia(serviceName),
  };

  const savedFindings = String(protocol.protocolSpecialtyStatus || '').trim();
  const savedConclusion = endoscopyConclusionFromProtocol(protocol);
  const findings = savedFindings || (injectTemplateBody ? template.findings : '');
  const conclusion = savedConclusion || (injectTemplateBody ? template.conclusion : '');
  const diagnoses = normalizeDiagnosesList({
    diagnoses: protocol.diagnoses,
    diagnosis: conclusion,
  });
  if (diagnoses[0] && !String(diagnoses[0].text || '').trim() && conclusion) {
    diagnoses[0] = { ...diagnoses[0], text: conclusion };
  }

  return {
    ...protocol,
    protocolSpecialty: 'endoscopy',
    protocolSpecialtyStatus: findings,
    protocolMeta: {
      title: savedMeta.title || defaults.title,
      equipment: savedMeta.equipment || defaults.equipment,
      caseNumber: savedMeta.caseNumber || defaults.caseNumber,
      department: savedMeta.department || defaults.department,
      anesthesia: savedMeta.anesthesia || defaults.anesthesia,
    },
    diagnosis: conclusion,
    diagnoses,
  };
}

export function applyEndoscopyTemplateToDraft(draft, serviceName = '', savedTemplates = []) {
  const template = resolveEndoscopyTemplate(serviceName, savedTemplates);
  return applySavedEndoscopyTemplateToDraft(draft, template);
}

export function buildEndoscopyPrintData({
  protocol,
  visit,
  patient,
  doctorName = '',
  serviceName = '',
  savedTemplates = [],
} = {}) {
  const name =
    serviceName ||
    visit?.serviceName ||
    visit?.service_name ||
    visit?.service?.name ||
    '';
  const filled = withEndoscopyProtocolDefaults(protocol, {
    serviceName: name,
    clientId: patient?.id || visit?.clientId || visit?.client_id,
    visit,
    savedTemplates,
    // Не подменяем сохранённый/пустой протокол шаблоном «норма» при печати
    injectTemplateBody: false,
  });
  const template = resolveEndoscopyTemplate(name, savedTemplates);
  const meta = normalizeEndoscopyMeta(filled.protocolMeta);

  return {
    title: meta.title || template.title,
    kind: resolveEndoscopyKind(name),
    date: formatEndoscopyDate(visit?.date),
    time: formatEndoscopyTime(visit?.time),
    patientLine: formatEndoscopyPatientLine(patient),
    phone: String(patient?.phone || visit?.clientPhone || '').trim(),
    equipment: meta.equipment,
    caseNumber: meta.caseNumber,
    department: meta.department,
    anesthesia: meta.anesthesia,
    findings: String(filled.protocolSpecialtyStatus || '').trim(),
    conclusion: endoscopyConclusionFromProtocol(filled),
    doctorName: String(
      doctorName || visit?.doctorName || visit?.doctor || visit?.doctor?.name || ''
    ).trim(),
  };
}
